import { client as apollo } from "@/graphql/vue-apollo";
import { unwrapNodes } from "@/helpers/graphql";
import cloneDeep from "lodash-es/cloneDeep";
import isEmpty from "lodash-es/isEmpty";
import isNull from "lodash-es/isNull";
import * as Sentry from "@sentry/vue";
import { businessLogic } from "@/config";

import addToCartMutation from "@/graphql/mutations/AddToCart.gql";
import removeFromCartMutation from "@/graphql/mutations/RemoveFromCart.gql";
import updateItemInCartMutation from "@/graphql/mutations/UpdateInCart.gql";
import cartQuery from "@/graphql/queries/Cart.gql";
import createCartMutation from "@/graphql/mutations/CreateCart.gql";

import { addToCartEvent } from "@/helpers/events";

const state = {
  cart: null,
  isCartOpen: false,
  multipassCheckoutUrl: null,
};

const mutations = {
  setCartState(state, value) {
    state.cart = value;
  },
  setCartOpenState(state, value) {
    state.isCartOpen = value;
  },
  setMultipassCheckoutUrl(state, value) {
    state.multipassCheckoutUrl = value;
  },
};

const getters = {
  cartId: (state) => {
    return state.cart?.id || null;
  },
  lines: (state) => {
    const lines = unwrapNodes(state.cart?.lines?.edges) || [];
    return cloneDeep(lines);
  },
};

const actions = {
  async checkCartRules({ getters }, id) {
    if (!isNull(businessLogic.maxCartTotalQuantity)) {
      const totalQuantity = getters.lines.reduce((n, { quantity }) => {
        return n + quantity;
      }, 0);

      if (totalQuantity >= businessLogic.maxCartTotalQuantity) {
        console.log("maxCartTotalQuantity");
        return false;
      }
    }

    if (!isNull(businessLogic.maxCartProductQuantity)) {
      const quantityByProduct = getters.lines
        .map((line) => ({
          id: line.merchandise.product.id,
          quantity: line.quantity,
        }))
        .reduce((b, a) => {
          const index = b.findIndex((arr) => arr.id == a.id);
          if (index > -1) {
            b[index].quantity += a.quantity;
          } else {
            b.push(a);
          }
          return b;
        }, []);

      const item = quantityByProduct.find((item) => item.id === id, 0);
      const quantity = item?.quantity;

      if (quantity >= businessLogic.maxCartProductQuantity) {
        console.log("maxCartProductQuantity");
        return false;
      }
    }

    return true;
  },
  async addCartItem({ commit, dispatch, state, rootState }, { id, quantity }) {
    let es_id = "-1";
    try {
      // eslint-disable-next-line no-undef
      es_id = EasySizeParametersDebug.easysize.pageview_id.toString();
    } catch (e) {
      console.log("EasySizeParametersDebug: " + e);
    }

    // We retry adding the cart item if when a user tries adding an item to the cart, the mutation fails.
    // EG where the cart no longer exists on the backend for whatever reason, the mutation would return an error.
    // The catch creates the cart, and then the while loop trys adding the item to cart
    // We limit the maxTries to 2 so we don't get an infinite loop.
    let count = 0;
    const maxTries = 2;

    const attributes = [{ key: " _es_id", value: es_id }];

    // eslint-disable-next-line no-constant-condition
    while (true) {
      try {
        const { data } = await apollo.mutate({
          mutation: addToCartMutation,
          variables: {
            cartId: state.cart.id,
            lines: [
              {
                quantity,
                merchandiseId: id,
                attributes,
              },
            ],
          },
        });

        commit("setCartState", data.cartLinesAdd.cart);

        try {
          addToCartEvent({
            sku: id,
            quantity: quantity,
            cart: data.cartLinesAdd.cart,
          });
        } catch (err) {
          console.log(err);
        }

        try {
          const cartLines = unwrapNodes(data.cartLinesAdd.cart.lines?.edges);
          if (!cartLines) {
            return;
          }

          const productId = cartLines[0].merchandise.product.id;
          if (!rootState.product.wishlist.includes(productId)) {
            return;
          }

          dispatch("product/removeFromWishList", productId, {
            root: true,
          });
        } catch (err) {
          console.log(err);
        }

        return;
      } catch (error) {
        if (++count === maxTries) {
          console.error("Cart: Item couldn't be added to cart", error);
        }
        console.warn("Cart: Cart could not be fetched, creating new cart");
        await dispatch("createCart");
      }
    }
  },
  async removeCartItem({ commit, state }, cardLineId) {
    const cartId = state.cart.id;

    try {
      const { data } = await apollo.mutate({
        mutation: removeFromCartMutation,
        variables: {
          cartId,
          lineIds: [cardLineId],
        },
      });

      commit("setCartState", data.cartLinesRemove.cart);
    } catch (error) {
      console.log(error);
    }
  },
  async updateCartItem({ state, commit }, line) {
    const cartId = state.cart.id;

    try {
      const { data } = await apollo.mutate({
        mutation: updateItemInCartMutation,
        variables: {
          cartId,
          lines: [line],
        },
      });

      commit("setCartState", data.cartLinesUpdate.cart);
    } catch (error) {
      console.log(error);
    }
  },
  async createCart({ commit, dispatch }) {
    try {
      const { data } = await apollo.mutate({
        mutation: createCartMutation,
      });

      localStorage.setItem("shopifyCartId", data.cartCreate.cart.id);
      commit("setCartState", data.cartCreate.cart);

      dispatch("multipassCheckoutUrl", null, { root: true });
    } catch (error) {
      console.log(error);
    }
  },
  async fetchCart({ commit, dispatch }) {
    const localStorageCartId = localStorage.getItem("shopifyCartId");

    if (!localStorageCartId) {
      console.log("Cart: Cart ID not set in location storage");
      dispatch("createCart");
      return;
    }

    try {
      const { data } = await apollo.query({
        query: cartQuery,
        variables: {
          id: localStorageCartId,
        },
      });

      if (!data.cart) {
        console.warn("Cart: Cart null, creating new cart");
        dispatch("createCart");
        return;
      }

      commit("setCartState", data.cart);

      dispatch("multipassCheckoutUrl", null, { root: true });
    } catch (error) {
      console.warn("Cart: Cart could not be fetched, creating new cart");
      dispatch("createCart");
    }
  },
  setCartOpen({ commit }, value) {
    commit("setCartOpenState", value);
  },
  multipassCheckoutUrl: {
    root: true,
    async handler({ commit, state, rootState }) {
      const email = rootState.app.customerEmail;

      if (!email) {
        return false;
      }

      const checkoutUrl = state.cart.checkoutUrl;
      const domain = new URL(checkoutUrl);
      const pathQuery = domain.pathname + domain.search;

      const input = {
        email: rootState.app.customerEmail,
        redirectUrl: pathQuery,
      };

      Sentry.addBreadcrumb({
        category: "auth",
        message: "multipassCheckoutUrl input " + JSON.stringify(input),
        level: "info",
      });

      const multipassResponse = await fetch(
        process.env.RATANDBOA_NODE_APP_URL +
          "/multipass?" +
          new URLSearchParams(input)
      );

      Sentry.addBreadcrumb({
        category: "auth",
        message:
          "multipassAccount response " + JSON.stringify(multipassResponse),
        level: "info",
      });

      const multipassData = await multipassResponse.json();
      if (isEmpty(multipassData)) {
        Sentry.captureMessage(
          "Empty multipassData cart: " + JSON.stringify(multipassData)
        );
        return;
      }

      commit("setMultipassCheckoutUrl", multipassData.url);
    },
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
